/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {AfterViewInit, ElementRef, HostListener, Injectable} from '@angular/core';

@Injectable({
    providedIn: 'root' // Or another Angular module where you want to provide this utility
})
export abstract class SvgArea implements AfterViewInit {
    svgElementRef: ElementRef;
    rootElementRef: ElementRef;
    scrollbarVerticalRef: ElementRef;
    scrollbarHorizontalRef: ElementRef;
    zoomFactor = Math.pow(2, .5); // square root of 2, so it doubles the resolution every other zoom level
    zoomLevel = 2;
    maxZoomLevel = 5;
    minZoomLevel = -3;
    displayWidth = 900;
    displayHeight = 600;
    scale = Math.pow(this.zoomFactor, this.zoomLevel);
    xOffset = 0;
    yOffset = 0;
    xExtent = this.displayWidth;
    yExtent = this.displayHeight;
    objectScale = 1;
    baseObjectScale = 1;
    mouseDownX: number | null = null;
    mouseDownY: number | null = null;
    originalOffsetX: number | null = null;
    originalOffsetY: number | null = null;
    adjustingScrollbarX = false;
    adjustingScrollbarY = false;
    fullSize = false;
    private zoomAnimationFrameId: number | null = null;
    private pendingZoomDeltaY: number | null = null;
    private pendingZoomClientX = 0;
    private pendingZoomClientY = 0;

    constructor() {
    }

    // This method will be called anytime the scale factor change
    abstract onScale(): void;
    // Hook for subclasses to request immediate re-render after internal state updates.
    protected requestRender(): void {
    }

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        // Recalculate the width of the SVG element when the window is resized
        this.calculateSvgSize();
    }

    calculateSvgSize() {
        if (!this.fullSize) {
            // Check if rootElementRef is initialized and has a nativeElement
            if (!this.rootElementRef || !this.rootElementRef.nativeElement) {
                return;
            }
            const containingDiv = this.rootElementRef.nativeElement.parentElement?.parentElement;
            if (!containingDiv) {
                return;
            }
            const boundingClientRect = containingDiv.getBoundingClientRect();
            if (!boundingClientRect) {
                return;
            }
            let rootWidth = boundingClientRect.width;
            let rootHeight = boundingClientRect.height;
            if (this.scrollbarVerticalRef) {
                // subtract the width of the right scrollbar
                rootWidth -= parseInt(this.scrollbarVerticalRef.nativeElement.style.width, 10);
            }
            this.displayWidth = rootWidth;
            if (this.scrollbarHorizontalRef) {
                // subtract the height of the bottom scrollbar
                rootHeight -= parseInt(this.scrollbarHorizontalRef.nativeElement.style.height, 10);
            }
            this.displayHeight = rootHeight;
        }
        this.updateScrollBars();
        this.adjustSvgSize();
    }
    abstract adjustSvgSize();

    private updateScrollBars(): void {
        if (this.fullSize) {
            // When we are in full size mode, there are no scrollbars
            return;
        }
        if (this.scrollbarVerticalRef && this.scrollbarVerticalRef.nativeElement.scrollTop !== (0 - this.yOffset)) {
            this.adjustingScrollbarY = true;
            this.scrollbarVerticalRef.nativeElement.scrollTop = 0 - this.yOffset;
        }
        if (this.scrollbarHorizontalRef && this.scrollbarHorizontalRef.nativeElement.scrollLeft !== (0 - this.xOffset)) {
            this.adjustingScrollbarX = true;
            this.scrollbarHorizontalRef.nativeElement.scrollLeft = 0 - this.xOffset;
        }
    }

    ngAfterViewInit(): void {
        // Run after the current call stack so view bindings have stabilized.
        queueMicrotask(() => {
            this.calculateSvgSize();
        });

        // Attaching mouse events listener with passive option to improve performance
        // adding the .bind(this) to the event listeners to ensures that 'this' inside the onMouse event
        // handlers refer to the component instance rather than the SVG element.
        this.svgElementRef.nativeElement.addEventListener('mousedown',  this.onMouseDown.bind(this),  { passive: true });
        this.svgElementRef.nativeElement.addEventListener('mousemove',  this.onMouseMove.bind(this),  { passive: true });
        this.svgElementRef.nativeElement.addEventListener('mouseup',    this.onMouseUp.bind(this),    { passive: true });
        this.svgElementRef.nativeElement.addEventListener('mouseleave', this.onMouseLeave.bind(this), { passive: true });
        this.svgElementRef.nativeElement.addEventListener('wheel',      this.onMouseWheel.bind(this)); // not passive!
    }

    private onMouseWheel(event: WheelEvent) {
        if (event.ctrlKey) {
            event.preventDefault();
            this.queueZoomUpdate(event);
        } else if (event.shiftKey) {
            // horizontal scroll:
            const oldOffset = this.xOffset;
            this.xOffset -= event.deltaY / this.scale;
            this.clipOffsets();
            if (oldOffset !== this.xOffset) {
                // If our scroll position changed, don't let the parent container also scroll:
                event.preventDefault();
            }
        } else  {
            // vertical scroll:
            const oldOffset = this.yOffset;
            this.yOffset -= event.deltaY / this.scale;
            this.clipOffsets();
            if (oldOffset !== this.yOffset) {
                // If our scroll position changed, don't let the parent container also scroll:
                event.preventDefault();
            }
        }
    }
    private queueZoomUpdate(event: WheelEvent): void {
        this.pendingZoomDeltaY = event.deltaY;
        this.pendingZoomClientX = event.clientX;
        this.pendingZoomClientY = event.clientY;
        if (this.zoomAnimationFrameId != null) {
            return;
        }
        this.zoomAnimationFrameId = requestAnimationFrame(() => {
            this.zoomAnimationFrameId = null;
            if (this.pendingZoomDeltaY == null) {
                return;
            }
            const deltaY = this.pendingZoomDeltaY;
            const clientX = this.pendingZoomClientX;
            const clientY = this.pendingZoomClientY;
            this.pendingZoomDeltaY = null;
            this.onMouseWheelZoom(deltaY, clientX, clientY);
        });
    }

    private onMouseWheelZoom(deltaY: number, clientX: number, clientY: number) {
        const zoomIn: boolean = (deltaY < 0);
        if (zoomIn) {
            if (this.zoomLevel === this.maxZoomLevel) {
                return;
            }
            this.zoomLevel++;
        } else {
            if (this.zoomLevel === this.minZoomLevel) {
                return;
            }
            this.zoomLevel--;
        }
        // Get the SVG element's bounding rectangle
        const svgRect = this.svgElementRef.nativeElement.getBoundingClientRect();
        // Calculate the relative coordinates inside the SVG element
        const relativeX = clientX - svgRect.left;
        const relativeY = clientY - svgRect.top;
        const unscaledX = (relativeX - this.xOffset) / this.scale;
        const unscaledY = (relativeY - this.yOffset) / this.scale;
        // Calculate the new scale factor
        this.scale = Math.pow(this.zoomFactor, this.zoomLevel);
        // After the zoom level is adjust the unscaledX & Y should be same as they were, so we adjust the x&yOffset
        // so the unscaledX&Y still match:
        this.xOffset = relativeX - unscaledX * this.scale;
        this.yOffset = relativeY - unscaledY * this.scale;
        this.clipOffsets();

        // as we are more zoomed in, increase the contained object scale,
        // but not by the same scale factor.
        this.objectScale = this.baseObjectScale * Math.pow(this.scale, .3);
        this.onScale();
        this.requestRender();
    }

    onMouseDown(event: MouseEvent) {
        this.originalOffsetX = this.xOffset;
        this.originalOffsetY = this.yOffset;
        this.mouseDownX = event.clientX;
        this.mouseDownY = event.clientY;
    }

    onMouseUp(event: MouseEvent) {
        this.mouseDownX = null;
        this.mouseDownY = null;
        this.originalOffsetX = null;
        this.originalOffsetY = null;
    }

    onMouseLeave(event: MouseEvent) {
        if (this.originalOffsetX && this.originalOffsetY) {
            this.xOffset = this.originalOffsetX;
            this.yOffset = this.originalOffsetY;
        }
    }

    onMouseMove(event: MouseEvent) {
        if (this.mouseDownX != null && this.mouseDownY != null &&
            this.originalOffsetX != null && this.originalOffsetY != null) {
            if (event.buttons === 1) {
                const moveX = event.clientX - this.mouseDownX;
                const moveY = event.clientY - this.mouseDownY;
                this.yOffset = this.originalOffsetY + moveY;
                this.xOffset = this.originalOffsetX + moveX;
                this.clipOffsets();
            } else {
                this.onMouseUp(event);
            }
        }
    }
    clipOffsets() {
        // clip to ensure we haven't exceeded the bounds of the SVG element:
        this.yOffset = Math.max((this.displayHeight - this.yExtent * this.scale), this.yOffset);
        this.xOffset = Math.max((this.displayWidth  - this.xExtent * this.scale), this.xOffset);
        // clip to ensure we haven't gone too far in the other direction:
        this.yOffset = Math.min(0, this.yOffset);
        this.xOffset = Math.min(0, this.xOffset);
        this.updateScrollBars();
    }
}
